import { useState, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { useColorScheme } from "./useColorScheme";

export default function ScrollHint({
  children,
  maxHeight = 200,
  darkMode,
  className = "",
  scrollText = "Scroll for more"
}: {
  children: React.ReactNode;
  maxHeight?: number;
  darkMode?: boolean;
  className?: string;
  scrollText?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [isNativeMobile, setIsNativeMobile] = useState(false);
  const { currentIsDark } = useColorScheme();

  // Use the darkMode prop if explicitly provided, otherwise use the system theme
  const isDarkTheme = darkMode !== undefined ? darkMode : currentIsDark;

  useEffect(() => {
    // Check if running in native mobile environment using Capacitor
    const checkNativeMobile = () => {
      // Use Capacitor.isNativePlatform() to check if running natively
      const isNative = Capacitor.isNativePlatform();

      // We can also check specific platforms if needed
      const isIOS = Capacitor.getPlatform() === "ios";
      const isAndroid = Capacitor.getPlatform() === "android";

      // Set state based on if we're in a native mobile environment
      setIsNativeMobile(isNative && (isIOS || isAndroid));
    };

    // Run detection on mount
    checkNativeMobile();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      setCanScrollUp(el.scrollTop > 5); // Add small threshold to prevent flicker
      setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 5);
    };

    // Handle scroll events and resize
    const observer = new ResizeObserver(check);
    observer.observe(el);
    el.addEventListener("scroll", check, { passive: true });

    // Initial check
    check();

    return () => {
      el.removeEventListener("scroll", check);
      observer.disconnect();
    };
  }, []);

  // Use different colors for dark mode
  const topColor = isDarkTheme ? "from-gray-800/60 to-transparent" : "from-white/60 to-transparent";
  const bottomColor = isDarkTheme
    ? "from-transparent to-gray-900/60"
    : "from-transparent to-white/60";

  return (
    <div className={`relative ${className}`}>
      <div ref={ref} style={{ maxHeight }} className="relative overflow-y-auto overscroll-contain">
        {children}
      </div>

      {/* Only show scroll indicators on native mobile platforms */}
      {isNativeMobile && (
        <>
          {/* Top gradient overlay - very subtle */}
          {canScrollUp && (
            <div
              className={`absolute top-0 left-0 w-full h-6 bg-gradient-to-b ${topColor} pointer-events-none`}></div>
          )}

          {/* Bottom gradient overlay - very subtle */}
          {canScrollDown && (
            <div
              className={`absolute bottom-0 left-0 w-full h-6 bg-gradient-to-t ${bottomColor} pointer-events-none`}></div>
          )}

          {/* Bottom indicator with arrow and text */}
          {canScrollDown && (
            <div
              className={`absolute bottom-1 left-1/2 transform -translate-x-1/2 transition-opacity duration-300 
                flex flex-col items-center pointer-events-none ${canScrollDown ? "opacity-80" : "opacity-0"}`}>
              <div
                className={`text-xs font-medium ${isDarkTheme ? "text-gray-300" : "text-gray-700"}`}>
                {scrollText}
              </div>
              <svg
                className={`w-4 h-4 ${isDarkTheme ? "text-gray-300" : "text-gray-700"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          )}
        </>
      )}
    </div>
  );
}
